import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import {
  Box,
  Typography,
  Grid,
  IconButton,
  Divider,
  Button,
  Switch,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';

import {
  Select,
  OutlinedTextField,
  OutlinedTextArea,
} from '../common/fields';

import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';

import TopSection from '../common/topSection';
import Loading from '../common/loading';
import FilePicker from '../common/filePicker';
import { addTyreAward,
  clearSingleRequestState,
  fetchTyreAward,
  fetchTyreAwardMagazines,
  fetchTyreAwards,
  selectTyreAwards,
  setFetchSingleState,
  updateTyreAward,
  updateTyreAwardTranslation } from './tyreAwardsSlice';
import { enqueueSnackbar } from '../snackbar/snackbarSlice';
import { selectTyreAwardsFilters } from '../app/appSlice';
import { selectCountriesWithNames } from '../countries/countriesSlice';

import TranslationDialog from './translationDialog';
import ActionBar from '../itemFamily/ActionBar';

const AWARD_BASE = {
  itemFamilyCode: '',
  productInfo: '',
  magazineId: '',
  magazineNumber: '',
  award: '',
  review: '',
  highlight: '',
  testSeasonId: '',
  publicationDate: '',
  published: '0'
}

const useStyles = makeStyles((theme) => ({
  image: {
    width: '100px',
    height: '56px',
    objectFit: 'contain',
  },
  flexRight: {
    marginLeft: 'auto',
  },
  checkIcon: {
    minWidth: '50px',
  },
  fieldName: {
    flex: 1,
    minWidth: 0,
  },
}));

const TyreAwardPage = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { itemOwnerId, awardId } = useParams();
  const history = useHistory();

  const [ awardData, setAwardData ] = useState(AWARD_BASE);
  const [ magazineCountry, setMagazineCountry ] = useState('');
  const [ editingTranslations, setEditingTranslations ] = useState(null);
  const [ editingCountry,setEditingCountry ] = useState(null);
  const [ dialogOpen, setDialogOpen ] = useState(false);
  const [ hasUnsavedChanges, setHasUnsavedChanges ] = useState(false);

  // State control for selects with autocomplete
  const [productInputValue, setProductInputValue] = useState('');

  const { awards,
    countries,
    magazines,
    testSeasons,
    fetchStatus,
    fetchError,
    fetchSingleStatus,
    fetchSingleError,
    saveStatus} =  useSelector(selectTyreAwards);
  const itemFamilies = useSelector(selectTyreAwardsFilters);
  const translationCountries = useSelector(selectCountriesWithNames).filter(c => c.id !== window.PIH.currentCountry && c.id !== 'ko_KR' );

  // Formatted elect options
  let sortedTestSeasons = useMemo(() => {
    if (testSeasons.length) {
      const sortedSeasons = [...testSeasons];
      return sortedSeasons.sort((a, b) => (a.year < b.year) ? 1 : -1).map(item => ({label: `${item.season.charAt(0).toUpperCase() + item.season.slice(1)} ${item.year}`, value: item.id}))
    }
    return []
  }, [testSeasons])

  const magazineOptions = useMemo(() => {
    if (magazines.length < 1 || !magazineCountry.length) return null;
    return magazines.filter(m => m.country === magazineCountry).map(item => ({label: item.name, value: item.id}))
  }, [magazines, magazineCountry])

  useEffect(() => {
    return () => {
      dispatch(clearSingleRequestState())
    }
  }, [])

  useEffect(() => {
    // We need all awards for magazines etc
    if (!awards.length) {
      if (fetchStatus !== 'loading') {
        dispatch(fetchTyreAwards({itemOwnerId}));
      }
    }
  }, [awards, itemOwnerId, dispatch, fetchStatus]);


  useEffect(() => {
    // Check if we have fetched single award data for translations
    if (awardId && awards.length > 0 && fetchSingleStatus !== 'loading') {
      const award = awards.find(a => a.id === awardId);
      if (!award || award && !award.singleFetched) {
        if (fetchSingleStatus !== 'failed') dispatch(fetchTyreAward({itemOwnerId, awardId}));
      } else if (award && award.singleFetched) {
        if (fetchSingleStatus !== 'succeeded') {
          // if we have already fetched this award before, set state to display data
          dispatch(setFetchSingleState({status: 'succeeded', error: null}))
        }
        setAwardData(award)
        const product = itemFamilies.find(i => i.code === award.itemFamilyCode);
        if (product) setProductInputValue(product.name)
      }
    }
  }, [awards, itemOwnerId, awardId, dispatch, fetchSingleStatus]);

  useEffect(() => {
    if(magazines.length < 1) {
      dispatch(fetchTyreAwardMagazines());
    } else if(awardData?.magazineId && magazines.length > 0){
      const magazine = magazines.find(m => m.id === awardData.magazineId);
      setMagazineCountry(magazine.country)
    }
  }, [magazines, awardData])

  // setState handlers
  const handleChange = (field, value) => {
    setAwardData({
      ...awardData,
      [field]: value
    });
    setHasUnsavedChanges(true);
  }

  const handleSwitchChange = event => {
    const switchState = event.target.checked ? '1' : '0';
    handleChange(event.target.name, switchState);
  };

  const handleFieldChange = event => {
    handleChange(event.target.name, event.target.value);
  }

  const handleMagazineChange = event => {
    const newMagazine = magazines.find(m => m.id === event.target.value)
    setAwardData({
      ...awardData,
      magazineId: newMagazine.id,
    })
  }

  const editTranslations = async (country) => {
    setEditingTranslations(awardData.translations);
    setEditingCountry(translationCountries.find(item => item.id === country))
    setDialogOpen(true);
  }

  const cancelChanges = () => {
    if (awardId) {
      setAwardData(awards.find(a => a.id === awardId));
      setHasUnsavedChanges(false);
    } else {
      history.push(`/pih/owner/${itemOwnerId}/tyre-awards`)
    }
  };

  const saveAward = async () => {
    const awardDataToInsert = Object.keys(AWARD_BASE).reduce((acc, key) => {
      return {...acc, [key]: awardData[key]};
    }, {});

    // Add magazine logo overriding file id if picked
    if (awardData.logoFile?.id) {
      awardDataToInsert.logoFileId = awardData.logoFile.id;
    }
    if(awardId) {
      // add id
      awardDataToInsert.id = awardId;

      const res = await dispatch(updateTyreAward({itemOwnerId, itemFamilyId: awardDataToInsert.itemFamilyCode, award: awardDataToInsert}));
      if (res?.payload.id) {
        dispatch(enqueueSnackbar({
          message: t('tyreAwards:awardUpdated'),
          options: {
            variant: 'success'
          }
        }));
      }
    } else {
      const res = await dispatch(addTyreAward({itemOwnerId, itemFamilyId: awardData.itemFamilyCode, award: awardDataToInsert}));
      if (res?.payload?.id) {
        dispatch(enqueueSnackbar({
          message: t('tyreAwards:awardAdded'),
          options: {
            variant: 'success'
          }
        }));
        history.push(`/pih/owner/${itemOwnerId}/tyre-award/${res.payload.id}`);
      } else {
        history.push(`/pih/owner/${itemOwnerId}/tyre-awards`);
      }
    }
  };

  const handleSaveTranslations = async (translations) => {
    const translationData = {};
    for (const lang of Object.keys(translations)) {
      translationData[lang] = {
        award: translations[lang].award,
        review: translations[lang].review,
        highlight: translations[lang].highlight,
        logoFileId: translations[lang].logoFile?.id ? translations[lang].logoFile.id : null
      }
    }
    // Create object to save, with different languages and id
    const awardToSave = {
      id: awardId,
      translations: translationData
    }
    setDialogOpen(false);
    const res = await dispatch(updateTyreAwardTranslation({itemOwnerId, award: awardToSave}));
    if (res?.payload) {
      dispatch(enqueueSnackbar({
        message: t('tyreAwards:translationUpdated'),
        options: {
          variant: 'success'
        }
      }));
    }
  }

  // Check if all needed properties for new translation have been added
  const languageHasBeenCreated = useCallback(
    (lang) => {
      const langData = awardData?.translations?.[lang];
      if (!langData) return false;
      if (langData?.review?.length > 0 && langData?.award?.length > 0) return true;
      return false;
    },
    [awardData.translations]
  )

  const editingMagazineLogo = useMemo(() => {
    const magazine = magazines.find(m => m.id === awardData.magazineId);
    if (magazine) return magazine.logo;
    return null;
  }, [awardData.magazineId, magazines])

  const succeededStatus = useMemo(() => {
    return (!awardId || awardId && awardData.id) &&
    fetchStatus === 'succeeded' &&
    (awardId ? fetchSingleStatus === 'succeeded' : true) &&
    saveStatus !== 'loading' &&
    magazines.length > 0
  }, [awardData, awardId, fetchStatus, fetchSingleStatus, saveStatus, magazines])

  return (<>
    <TopSection>
      <Typography variant="h4" component="h1">{awardId ? t('tyreAwards:edit') :t('tyreAwards:add')}</Typography>
    </TopSection>
    <Box p={3.5} pb="70px">
      {fetchStatus === 'loading' || fetchSingleStatus === 'loading' || saveStatus === 'loading' || magazines.length < 1 ? <Loading /> : null}
      {fetchStatus === 'failed' || fetchSingleStatus === 'failed' ? <Alert severity="error">
        {fetchSingleStatus ==='failed' ? t(`errors:${fetchSingleError.message}`) : t(`errors:${fetchError}`)}
      </Alert> : null}
      {succeededStatus ? <Grid container spacing={7}>
        <Grid item xs={12} md={8}>
          {awardData ? <>
            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('tyreAwards:publication')}</Typography></Box>

              <Box mb={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={6}>
                    <Select
                      label={t('tyreAwards:testSeason')}
                      name="testSeasonId"
                      value={awardData.testSeasonId}
                      options={sortedTestSeasons}
                      onChange={handleFieldChange}
                      disabled={false}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box mb={5}>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={6}>
                    <Select
                      label={t('tyreAwards:country')}
                      name="country"
                      value={magazineCountry}
                      options={countries.map(item => ({label: item.nameEnglish, value: item.id}))}
                      onChange={(e) => setMagazineCountry(e.target.value)}
                      disabled={false}
                    /></Grid>
                  <Grid item xs={12} lg={6}>
                    {magazineOptions ? <Select
                      label={t('tyreAwards:magazine')}
                      name="magazineId"
                      value={awardData.magazineId}
                      options={magazineOptions}
                      onChange={handleMagazineChange}
                      disabled={false}
                    /> : null }
                  </Grid>
                </Grid>
              </Box>

              <Box mb={3}>
                <OutlinedTextField
                  label={t('tyreAwards:issue')}
                  name="magazineNumber"
                  value={awardData.magazineNumber}
                  onChange={handleFieldChange}
                  disabled={false}
                  maxLength={25}
                  helperText={t('tyreAwards:issueHelper')}
                />
              </Box>

              <Box mb={3}>
                <Typography variant="overline">{t('tyreAwards:magazineLogo')}</Typography><br/>
                {editingMagazineLogo ? <img
                  className={classes.image}
                  src={editingMagazineLogo}
                  srcSet={editingMagazineLogo}
                  alt={t('tyreAwards:magazineLogoAlt')}
                /> : null}
              </Box>

              <Box mb={3}>
                <Typography variant="overline">{t('tyreAwards:overrideLogo')}</Typography><br/>
                <FilePicker fieldName={t('tyreAwards:overrideLogo')}
                  rootFolderId={'52c14f5f2b089fc2b66b98e7'}
                  fieldValue={awardData.logoFile}
                  setFieldValue={(file) => handleChange('logoFile', file)}
                  disabled={false} />
              </Box>
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('tyreAwards:productTitle')}</Typography></Box>
              <Box mb={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={6}>
                    <Autocomplete
                      options={itemFamilies.map(item => ({label: item.name, value: item.code}))}
                      value={awardData.itemFamilyCode}
                      onChange={(event, newValue) => {
                        if (newValue) handleChange('itemFamilyCode', newValue.value);
                      }}
                      inputValue={productInputValue}
                      onInputChange={(event, newInputValue) => {
                        setProductInputValue(newInputValue);
                      }}
                      getOptionSelected={(opt, val) => opt.value === val}
                      name="itemFamilyCode"
                      getOptionLabel={(option) => option.label ? option.label : option?
                        itemFamilies.find(i => i.code === awardData.itemFamilyCode)?.name : ''}
                      renderInput={(params) => <OutlinedTextField
                        label={t('tyreAwards:itemFamily')}
                        {...params}
                      />}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <OutlinedTextField
                      label={t('tyreAwards:productInfo')}
                      name="productInfo"
                      value={awardData.productInfo}
                      onChange={handleFieldChange}
                      disabled={false}
                      maxLength={25}
                      helperText={t('tyreAwards:productInfoHelper')}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('tyreAwards:awardTitle')}</Typography></Box>

              <Box mb={3}>
                <OutlinedTextField
                  label={t('tyreAwards:awardName')}
                  name="award"
                  value={awardData.award}
                  onChange={handleFieldChange}
                  disabled={false}
                  maxLength={25}
                  helperText={t('tyreAwards:awardNameHelper')}
                />
              </Box>

              <Box mb={3}>
                <OutlinedTextArea
                  label={t('tyreAwards:reviewInfo')}
                  name="review"
                  value={awardData.review}
                  onChange={handleFieldChange}
                  disabled={false}
                  maxLength={500}
                  helperText={t('tyreAwards:reviewHelper')}
                />
              </Box>
              <Box mb={3}>
                <OutlinedTextArea
                  label={t('tyreAwards:highlight')}
                  name="highlight"
                  value={awardData.highlight}
                  onChange={handleFieldChange}
                  disabled={false}
                  maxLength={500}
                  helperText={t('tyreAwards:highlightHelper')}
                />
              </Box>

              <Box mb={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={6}>
                    <OutlinedTextField
                      label={t('tyreAwards:publicationDate')}
                      type="date"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      name="publicationDate"
                      value={awardData.publicationDate}
                      onChange={handleFieldChange}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    {awardId ? <>
                      <Typography variant="overline">{t('tyreAwards:isPublished')}</Typography> <Typography variant="caption">{t('tyreAwards:publishedInfo')}</Typography><br/>
                      {t('tyreAwards:no')}
                      <Switch inputProps={{ 'aria-label': t('tyreAwards:isPublished') }}
                        color="primary"
                        name="published"
                        checked={awardData.published === '1'}
                        onChange={handleSwitchChange} />
                      {t('tyreAwards:yes')}
                    </> : null}
                  </Grid>
                </Grid>
              </Box>

            </Box>

            { awardId ? <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('tyreAwards:translations')}</Typography></Box>

              <Box mb={3}>
                {translationCountries.map(country => (<Box key={country.id}>
                  <Grid container spacing={2} alignItems={'center'}>
                    <Grid item className={classes.checkIcon}>
                      {languageHasBeenCreated(country.id) ? <CheckIcon color="primary"/> : null}
                    </Grid>
                    <Grid item className={classes.fieldName}>
                      <Typography variant="subtitle1" component="span">{country.name}</Typography><br />
                    </Grid>
                    <Grid item className={classes.flexRight}>
                      <IconButton aria-label={t('tyreAwards:editTranslation')}
                        onClick={() => editTranslations(country.id)}
                        title={t('tyreAwards:editTranslation')}
                        color="primary"
                        disabled={false}>
                        <EditIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                  <Divider />
                </Box>
                ))}
              </Box>
            </Box> : null }
          </> : null}

        </Grid>
      </Grid>: null}
      <TranslationDialog open={dialogOpen}
        handleSave={handleSaveTranslations}
        handleClose={() => setDialogOpen(false)}
        awards={editingTranslations}
        country={editingCountry}
        countries={translationCountries}
      />

    </Box>
    <ActionBar>
      <Button variant="outlined" color="primary" onClick={cancelChanges}>
        {awardId ? t('common:cancelChanges') : t('common:close')}
      </Button>
      <Button variant="contained" color="primary" onClick={saveAward} disabled={!hasUnsavedChanges || saveStatus === 'loading'}>
        {awardId ? t('common:saveChanges') : t('tyreAwards:addNew')}
      </Button>

    </ActionBar>
  </>
  );
};

export default TyreAwardPage;
